(() => {
  'use strict';

  const SELECTOR = '[data-bw-simulador]';

  class BetterwareSimulator {
    constructor(root) {
      this.root = root;
      this.defaultCatalog = root.dataset.defaultCatalog || 'C7';
      this.state = {
        catalogo: this.defaultCatalog,
        semanaInicio: null,
        nivel: root.dataset.defaultLevel || 'BASE',
        invitoNuevo: root.dataset.defaultInvited === 'true',
        compras: {}
      };

      this.catalogos = [];
      this.reglas = null;
      this.lastResult = null;
      this.skipNextAutoSelect = false;

      this.currency = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
      });

      this.number = new Intl.NumberFormat('es-MX');

      this.elements = {
        catalogo: root.querySelector('[data-field="catalogo"]'),
        semanaInicio: root.querySelector('[data-field="semanaInicio"]'),
        invitado: root.querySelector('[data-field="invitoNuevo"]'),
        desktop: root.querySelector('[data-role="desktop-table"]'),
        mobile: root.querySelector('[data-role="mobile-cards"]'),
        summary: root.querySelector('[data-role="summary"]'),
        status: root.querySelector('[data-role="status"]')
      };

      this.bindEvents();
      this.updateLevelButtons();
      this.updateInviteLabels();
      this.renderEmptySummary();
      this.loadConfig();
    }

    bindEvents() {
      this.elements.catalogo.addEventListener('change', (event) => {
        this.state.catalogo = event.target.value;
        this.handleCatalogChange();
      });

      this.elements.semanaInicio.addEventListener('change', (event) => {
        this.state.semanaInicio = Number(event.target.value);
        this.state.compras = {};
        this.calculate();
      });

      this.elements.invitado.addEventListener('change', (event) => {
        this.state.invitoNuevo = event.target.checked;
        this.updateInviteLabels();
        this.calculate();
      });

      this.root.addEventListener('click', (event) => {
        const levelButton = event.target.closest('[data-level]');
        if (levelButton && this.root.contains(levelButton)) {
          this.state.nivel = levelButton.dataset.level;
          this.updateLevelButtons();
          this.calculate();
        }
      });

      this.root.addEventListener('input', (event) => {
        const input = event.target.closest('[data-week-input]');
        if (!input || !this.root.contains(input)) return;

        const week = String(input.dataset.weekInput);
        const digitsOnly = input.value.replace(/[^\d]/g, '');

        this.state.compras[week] = digitsOnly === '' ? '' : Math.max(0, Number(digitsOnly));
        this.calculate();
      });

      this.root.addEventListener('focusin', (event) => {
        const input = event.target.closest('[data-week-input]');
        if (!input) return;

        if (this.skipNextAutoSelect) {
          this.skipNextAutoSelect = false;
          return;
        }

        window.setTimeout(() => input.select(), 10);
      });
    }

    loadConfig() {
      this.setStatus('loading', 'Cargando configuración del simulador…');

      try {
        const data = this.readEmbeddedConfig();
        this.applyConfig(data);
        this.populateCatalogs();
        this.clearStatus();
      } catch (error) {
        this.setStatus(
          'error',
          error instanceof Error ? error.message : 'No fue posible cargar la configuración.'
        );
      }
    }

    readEmbeddedConfig() {
      const script = this.root.querySelector('[data-role="config"]');
      const raw = script?.textContent?.trim();

      if (!raw) {
        throw new Error('Selecciona un metaobjeto de configuración para este simulador en el editor de temas.');
      }

      try {
        return JSON.parse(raw);
      } catch (error) {
        throw new Error('No fue posible interpretar la configuración del simulador.');
      }
    }

    applyConfig(data) {
      if (!data || data.activo === false) {
        throw new Error('El simulador no está activo actualmente.');
      }

      if (!Array.isArray(data.catalogos) || data.catalogos.length === 0) {
        throw new Error('La configuración no contiene catálogos.');
      }

      this.catalogos = data.catalogos
        .map((catalog) => ({
          id: String(catalog.id),
          nombre: String(catalog.nombre),
          inicio: Number(catalog.inicio),
          fin: Number(catalog.fin)
        }))
        .filter((catalog) =>
          catalog.id &&
          Number.isInteger(catalog.inicio) &&
          Number.isInteger(catalog.fin) &&
          catalog.fin >= catalog.inicio
        );

      if (this.catalogos.length === 0) {
        throw new Error('No existen catálogos válidos para mostrar.');
      }

      const niveles = Array.isArray(data.niveles) && data.niveles.length > 0
        ? data.niveles.map(String)
        : Object.keys(data.multiplicadoresDescuento || {});

      this.reglas = {
        niveles,
        multiplicadoresDescuento: data.multiplicadoresDescuento || {},
        compraMinimaPuntosBase: Number(data.compraMinimaPuntosBase) || 0,
        compraMinimaActiva: Number(data.compraMinimaActiva) || 0,
        puntosBasePorPeso: Number(data.puntosBasePorPeso) || 1,
        costoRegalo: Number(data.costoRegalo) || 0,
        multiplicadorBonoInvitado: Number(data.multiplicadorBonoInvitado) || 1,
        reglasRegalo: Array.isArray(data.reglasRegalo) ? data.reglasRegalo : [],
        reglasVelocimetro: Array.isArray(data.reglasVelocimetro) ? data.reglasVelocimetro : []
      };

      if (!niveles.includes(this.state.nivel)) {
        this.state.nivel = niveles[0] || this.state.nivel;
        this.updateLevelButtons();
      }
    }

    populateCatalogs() {
      this.elements.catalogo.innerHTML = this.catalogos
        .map((catalog) => (
          `<option value="${this.escapeHtml(catalog.id)}">${this.escapeHtml(catalog.nombre)} (S${catalog.inicio} a S${catalog.fin})</option>`
        ))
        .join('');

      const catalogExists = this.catalogos.some((catalog) => catalog.id === this.defaultCatalog);
      this.state.catalogo = catalogExists ? this.defaultCatalog : this.catalogos[0].id;
      this.elements.catalogo.value = this.state.catalogo;

      this.handleCatalogChange(true);
    }

    handleCatalogChange(initial = false) {
      const catalog = this.currentCatalog();
      if (!catalog) return;

      this.populateStartWeeks(catalog);

      if (!initial) {
        this.state.compras = {};
      }

      this.calculate();
    }

    populateStartWeeks(catalog) {
      const weeks = [];
      for (let week = catalog.inicio; week <= catalog.fin; week += 1) {
        weeks.push(week);
      }

      this.elements.semanaInicio.innerHTML = weeks
        .map((week) => `<option value="${week}">Semana ${week}</option>`)
        .join('');

      this.elements.semanaInicio.disabled = false;

      const currentIsValid = weeks.includes(Number(this.state.semanaInicio));
      this.state.semanaInicio = currentIsValid
        ? Number(this.state.semanaInicio)
        : catalog.inicio;

      this.elements.semanaInicio.value = String(this.state.semanaInicio);
    }

    currentCatalog() {
      return this.catalogos.find((catalog) => catalog.id === this.state.catalogo) || null;
    }

    activeWeeks() {
      const catalog = this.currentCatalog();
      if (!catalog || !Number.isInteger(this.state.semanaInicio)) return [];

      const weeks = [];
      for (let week = this.state.semanaInicio; week <= catalog.fin; week += 1) {
        weeks.push(week);
      }
      return weeks;
    }

    updateLevelButtons() {
      this.root.querySelectorAll('[data-level]').forEach((button) => {
        const active = button.dataset.level === this.state.nivel;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    updateInviteLabels() {
      const yes = this.root.querySelector('[data-invite-label="si"]');
      const no = this.root.querySelector('[data-invite-label="no"]');

      yes?.classList.toggle('is-active', this.state.invitoNuevo);
      no?.classList.toggle('is-active', !this.state.invitoNuevo);
    }

    renderDesktopTable(data) {
      const weeks = this.activeWeeks();

      if (weeks.length === 0) {
        this.elements.desktop.innerHTML = '';
        return;
      }

      const rows = data?.resultados || [];
      const totals = data?.totales || this.emptyTotals();
      const byWeek = new Map(rows.map((row) => [Number(row.semanaId), row]));

      this.elements.desktop.innerHTML = `
        <div class="bw-simulador__table-scroll">
          <table class="bw-simulador__table">
            <thead>
              <tr>
                <th class="bw-simulador__sticky bw-simulador__concept-head">Concepto</th>
                ${weeks.map((week) => `<th>S${week}</th>`).join('')}
                <th class="bw-simulador__total-head">Total Catálogo</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="bw-simulador__sticky bw-simulador__concept bw-simulador__concept--strong">Monto de Compra</td>
                ${weeks.map((week) => `
                  <td class="bw-simulador__purchase-cell">
                    ${this.weekInput(week, 'desktop')}
                  </td>
                `).join('')}
                <td class="bw-simulador__total-cell bw-simulador__total-cell--green">
                  ${this.currency.format(totals.compra || 0)}
                </td>
              </tr>

              <tr class="bw-simulador__row-soft">
                <td class="bw-simulador__sticky bw-simulador__concept">Descuento Neto*</td>
                ${weeks.map((week) => `
                  <td>${this.currency.format(byWeek.get(week)?.descNeto || 0)}</td>
                `).join('')}
                <td class="bw-simulador__total-cell">
                  ${this.currency.format(totals.descNeto || 0)}
                </td>
              </tr>

              <tr>
                <td class="bw-simulador__sticky bw-simulador__section-label bw-simulador__section-label--orange">
                  Ganancia de Producto a Precio de Regalo
                </td>
                <td colspan="${weeks.length + 1}" class="bw-simulador__section-note bw-simulador__section-note--orange">
                  Valor de los productos recibidos menos el pago fijo de ${this.currency.format(this.reglas?.costoRegalo || 0)}.
                </td>
              </tr>

              <tr>
                <td class="bw-simulador__sticky bw-simulador__concept bw-simulador__concept--indent">Valor Catálogo</td>
                ${weeks.map((week) => {
                  const value = byWeek.get(week)?.precioCat || 0;
                  return `<td class="bw-simulador__strike">${value > 0 ? this.currency.format(value) : '-'}</td>`;
                }).join('')}
                <td>${this.currency.format(totals.precioCat || 0)}</td>
              </tr>

              <tr>
                <td class="bw-simulador__sticky bw-simulador__concept bw-simulador__concept--indent">Pago Fijo</td>
                ${weeks.map((week) => {
                  const value = byWeek.get(week)?.pagoRegalo || 0;
                  return `<td class="bw-simulador__negative">${value > 0 ? `-${this.currency.format(value)}` : '-'}</td>`;
                }).join('')}
                <td class="bw-simulador__negative">-${this.currency.format(totals.pagoRegalo || 0)}</td>
              </tr>

              <tr class="bw-simulador__row-orange">
                <td class="bw-simulador__sticky bw-simulador__concept bw-simulador__concept--indent bw-simulador__orange">
                  Descuento de producto a precio de regalo
                </td>
                ${weeks.map((week) => {
                  const value = byWeek.get(week)?.gananciaRegalo || 0;
                  return `<td class="bw-simulador__orange bw-simulador__strong">${value > 0 ? this.currency.format(value) : '-'}</td>`;
                }).join('')}
                <td class="bw-simulador__total-cell">
                  ${this.currency.format(totals.gananciaRegalo || 0)}
                </td>
              </tr>

              <tr>
                <td class="bw-simulador__sticky bw-simulador__section-label bw-simulador__section-label--green">
                  Puntos Betterware
                </td>
                <td colspan="${weeks.length + 1}" class="bw-simulador__section-note bw-simulador__section-note--green">
                  ${this.reglas?.puntosBasePorPeso ?? 1} PB por peso de compra + Bonos del Velocímetro.
                </td>
              </tr>

              <tr>
                <td class="bw-simulador__sticky bw-simulador__concept bw-simulador__concept--indent">1 PB x $1</td>
                ${weeks.map((week) => `<td>${this.number.format(byWeek.get(week)?.basePB || 0)}</td>`).join('')}
                <td>${this.number.format(totals.basePB || 0)}</td>
              </tr>

              <tr class="bw-simulador__row-green">
                <td class="bw-simulador__sticky bw-simulador__concept bw-simulador__concept--indent bw-simulador__green bw-simulador__strong">
                  Velocímetro
                </td>
                ${weeks.map((week) => `<td class="bw-simulador__green bw-simulador__strong">${this.number.format(byWeek.get(week)?.velocimetro || 0)}</td>`).join('')}
                <td class="bw-simulador__strong">${this.number.format(totals.velocimetro || 0)}</td>
              </tr>

              ${this.state.invitoNuevo ? `
                <tr class="bw-simulador__row-purple">
                  <td colspan="${weeks.length + 1}" class="bw-simulador__bonus-label">
                    Bono Acelera x2 (Por Invitado) -&gt;
                  </td>
                  <td class="bw-simulador__purple bw-simulador__strong">
                    +${this.number.format(totals.extraInvitado || 0)}
                  </td>
                </tr>
              ` : ''}
            </tbody>
          </table>
        </div>
      `;
    }

    renderMobileCards(data) {
      const weeks = this.activeWeeks();
      const rows = data?.resultados || [];
      const totals = data?.totales || this.emptyTotals();
      const byWeek = new Map(rows.map((row) => [Number(row.semanaId), row]));

      this.elements.mobile.innerHTML = `
        <div class="bw-simulador__mobile-stack">
          ${weeks.map((week) => {
            const row = byWeek.get(week) || {};
            return `
              <article class="bw-simulador__week-card">
                <header class="bw-simulador__week-card-header">SEMANA S${week}</header>
                <div class="bw-simulador__week-card-body">
                  <div class="bw-simulador__mobile-purchase">
                    <label>Monto de Compra</label>
                    ${this.weekInput(week, 'mobile')}
                  </div>

                  <div class="bw-simulador__mobile-result bw-simulador__mobile-result--neutral">
                    <span>Descuento Neto*</span>
                    <strong>${this.currency.format(row.descNeto || 0)}</strong>
                  </div>

                  <div class="bw-simulador__mobile-result bw-simulador__mobile-result--orange">
                    <span class="bw-simulador__mobile-kicker">Regalo por Constancia</span>
                    <div><span>Valor Catálogo:</span><span class="bw-simulador__strike">${row.precioCat ? this.currency.format(row.precioCat) : '-'}</span></div>
                    <div><span>Pago Fijo:</span><span class="bw-simulador__negative">${row.pagoRegalo ? `-${this.currency.format(row.pagoRegalo)}` : '-'}</span></div>
                    <div class="bw-simulador__mobile-result-total">
                      <span>Ganancia de Producto a Precio de Regalo:</span>
                      <strong>${row.gananciaRegalo ? this.currency.format(row.gananciaRegalo) : '-'}</strong>
                    </div>
                  </div>

                  <div class="bw-simulador__mobile-result bw-simulador__mobile-result--green">
                    <span class="bw-simulador__mobile-kicker">Puntos Betterware</span>
                    <div><span>1 PB x $1:</span><span>${this.number.format(row.basePB || 0)} PB</span></div>
                    <div class="bw-simulador__mobile-result-total">
                      <span>Velocímetro</span>
                      <strong>${this.number.format(row.velocimetro || 0)} PB</strong>
                    </div>
                  </div>
                </div>
              </article>
            `;
          }).join('')}

          <article class="bw-simulador__catalog-card">
            <header class="bw-simulador__catalog-card-header">
              <strong>Resumen Total Catálogo</strong>
              <span>Acumulado equivalente a la columna de escritorio</span>
            </header>
            <div class="bw-simulador__catalog-card-body">
              <div class="bw-simulador__catalog-line">
                <span>Monto total de compra:</span>
                <strong class="bw-simulador__blue">${this.currency.format(totals.compra || 0)}</strong>
              </div>
              <div class="bw-simulador__catalog-line">
                <span>Descuento Neto estimado:</span>
                <strong>${this.currency.format(totals.descNeto || 0)}</strong>
              </div>

              <div class="bw-simulador__mobile-result bw-simulador__mobile-result--orange">
                <span class="bw-simulador__mobile-kicker">Producto a Precio de Regalo</span>
                <div><span>Valor Catálogo:</span><span class="bw-simulador__strike">${this.currency.format(totals.precioCat || 0)}</span></div>
                <div><span>Pago Fijo:</span><span class="bw-simulador__negative">-${this.currency.format(totals.pagoRegalo || 0)}</span></div>
                <div class="bw-simulador__mobile-result-total">
                  <span>Beneficio estimado por precio de regalo:</span>
                  <strong>${this.currency.format(totals.gananciaRegalo || 0)}</strong>
                </div>
              </div>

              <div class="bw-simulador__mobile-result bw-simulador__mobile-result--green">
                <span class="bw-simulador__mobile-kicker">Puntos Betterware (PB)</span>
                <div><span>PB por compra:</span><span>${this.number.format(totals.basePB || 0)} PB</span></div>
                <div><span>Bono Velocímetro:</span><span>${this.number.format(totals.velocimetro || 0)} PB</span></div>
                ${this.state.invitoNuevo ? `<div class="bw-simulador__purple"><span>Bono Acelera x2:</span><span>+${this.number.format(totals.extraInvitado || 0)} PB</span></div>` : ''}
                <div class="bw-simulador__mobile-result-total">
                  <span>Puntos totales estimados:</span>
                  <strong>${this.number.format(totals.pbFinal || 0)} PB</strong>
                </div>
              </div>
            </div>
          </article>
        </div>
      `;
    }

    renderSummary(data) {
      const totals = data?.totales || this.emptyTotals();

      this.elements.summary.innerHTML = `
        <article class="bw-simulador__summary-card">
          <span class="bw-simulador__summary-decoration bw-simulador__summary-decoration--orange"></span>
          <h3>BENEFICIO ESTIMADO</h3>
          <strong class="bw-simulador__summary-value bw-simulador__orange">
            ${this.currency.format(totals.beneficioEfectivo || 0)}
          </strong>
          <span class="bw-simulador__summary-pill">Descuento + Regalos</span>
        </article>

        <article class="bw-simulador__summary-card bw-simulador__summary-card--featured">
          <span class="bw-simulador__summary-decoration bw-simulador__summary-decoration--white"></span>
          <h3>PORCENTAJE DE GANANCIA ESTIMADA</h3>
          <div class="bw-simulador__percentage">
            <strong>${Number(totals.porcentajeGanancia || 0).toFixed(1)}</strong><span>%</span>
          </div>
          <p>
            La proyección representa hasta el
            <strong>${Number(totals.porcentajeGanancia || 0).toFixed(1)}%</strong>
            de tu compra total estimada
            (${this.currency.format(totals.compra || 0)}),
            sujeto al cumplimiento de las reglas comerciales.
          </p>
        </article>

        <article class="bw-simulador__summary-card">
          <span class="bw-simulador__summary-decoration bw-simulador__summary-decoration--green"></span>
          <h3>PUNTOS TOTALES</h3>
          <div class="bw-simulador__points">
            <strong>${this.number.format(totals.pbFinal || 0)}</strong>
            <span>PB</span>
          </div>
          <span class="bw-simulador__summary-pill">¡Listo para canjear premios!</span>
        </article>
      `;
    }

    renderEmptySummary() {
      this.renderSummary({ totales: this.emptyTotals() });
    }

    weekInput(week, context) {
      const value = this.state.compras[String(week)];
      const safeValue = value === undefined || value === '' ? '' : String(value);

      return `
        <input
          class="bw-simulador__week-input bw-simulador__week-input--${context}"
          type="text"
          inputmode="decimal"
          autocomplete="off"
          data-week-input="${week}"
          value="${this.escapeHtml(safeValue)}"
          placeholder="$0"
          aria-label="Monto de compra semana ${week}"
        >
      `;
    }

    validateState() {
      if (!this.reglas) return 'La configuración del simulador aún no está lista.';
      if (!this.state.catalogo) return 'Selecciona un catálogo.';
      if (!Number.isInteger(this.state.semanaInicio)) return 'Selecciona una semana inicial.';
      if (!this.reglas.niveles.includes(this.state.nivel)) return 'Selecciona un nivel válido.';

      for (const week of this.activeWeeks()) {
        const raw = this.state.compras[String(week)];
        if (raw === '' || raw === undefined) continue;
        if (!Number.isFinite(Number(raw)) || Number(raw) < 0) {
          return `El monto de la semana ${week} no es válido.`;
        }
      }

      return null;
    }

    calculate() {
      const validationError = this.validateState();

      if (validationError) {
        this.setStatus('error', validationError);
        return;
      }

      const focusState = this.captureFocusState();
      const result = this.computeResult();
      this.lastResult = result;

      this.renderDesktopTable(result);
      this.renderMobileCards(result);
      this.renderSummary(result);
      this.restoreFocusState(focusState);
      this.clearStatus();
    }

    captureFocusState() {
      const active = document.activeElement;
      if (!active || !this.root.contains(active) || !active.matches('[data-week-input]')) {
        return null;
      }

      const atEnd = active.selectionStart === active.value.length && active.selectionEnd === active.value.length;

      return {
        week: active.dataset.weekInput,
        context: active.classList.contains('bw-simulador__week-input--desktop') ? 'desktop' : 'mobile',
        selectionStart: active.selectionStart,
        selectionEnd: active.selectionEnd,
        atEnd
      };
    }

    restoreFocusState(focusState) {
      if (!focusState) return;

      const selector = `[data-week-input="${CSS.escape(focusState.week)}"].bw-simulador__week-input--${focusState.context}`;
      const input = this.root.querySelector(selector);

      if (!input) return;

      this.skipNextAutoSelect = true;
      input.focus();

      const length = input.value.length;
      const start = focusState.atEnd ? length : Math.min(focusState.selectionStart, length);
      const end = focusState.atEnd ? length : Math.min(focusState.selectionEnd, length);

      try {
        input.setSelectionRange(start, end);
      } catch (error) {
        // Algunos navegadores/tipos de input no soportan rangos de selección; el foco ya quedó restaurado.
      }
    }

    computeResult() {
      const weeks = this.activeWeeks();
      const multiplicador = this.reglas.multiplicadoresDescuento[this.state.nivel] || 0;
      const totales = this.emptyTotals();

      let semanaActiva = 0;
      const resultados = weeks.map((week) => {
        const raw = this.state.compras[String(week)];
        const monto = raw === '' || raw === undefined ? 0 : Math.max(0, Number(raw) || 0);

        const descNeto = Math.round(monto * multiplicador);
        const basePB = monto >= this.reglas.compraMinimaPuntosBase
          ? Math.floor(monto * this.reglas.puntosBasePorPeso)
          : 0;

        let precioCat = 0;
        let pagoRegalo = 0;
        let gananciaRegalo = 0;
        let velocimetro = 0;

        if (monto >= this.reglas.compraMinimaActiva) {
          const bracketRegalo = this.reglas.reglasRegalo.find((regla) =>
            monto >= regla.min && (regla.max == null || monto <= regla.max)
          );
          const bracketVelo = this.reglas.reglasVelocimetro.find((regla) =>
            monto >= regla.min && (regla.max == null || monto <= regla.max)
          );

          if (bracketRegalo?.valores?.length) {
            const index = Math.min(semanaActiva, bracketRegalo.valores.length - 1);
            precioCat = bracketRegalo.valores[index] || 0;
          }

          if (bracketVelo?.valores?.length) {
            const index = Math.min(semanaActiva, bracketVelo.valores.length - 1);
            velocimetro = bracketVelo.valores[index] || 0;
          }

          pagoRegalo = this.reglas.costoRegalo;
          gananciaRegalo = precioCat - pagoRegalo;

          semanaActiva += 1;
        }

        totales.compra += monto;
        totales.descNeto += descNeto;
        totales.precioCat += precioCat;
        totales.pagoRegalo += pagoRegalo;
        totales.gananciaRegalo += gananciaRegalo;
        totales.basePB += basePB;
        totales.velocimetro += velocimetro;

        return { semanaId: week, monto, descNeto, precioCat, pagoRegalo, gananciaRegalo, basePB, velocimetro };
      });

      totales.extraInvitado = this.state.invitoNuevo
        ? Math.round(totales.velocimetro * this.reglas.multiplicadorBonoInvitado)
        : 0;
      totales.pbFinal = totales.basePB + totales.velocimetro + totales.extraInvitado;
      totales.beneficioEfectivo = totales.descNeto + totales.gananciaRegalo;

      const inversionReal = totales.compra + totales.pagoRegalo;
      totales.porcentajeGanancia = inversionReal > 0
        ? Number(((totales.beneficioEfectivo / inversionReal) * 100).toFixed(1))
        : 0;

      return { resultados, totales };
    }

    setStatus(type, message) {
      this.elements.status.hidden = false;
      this.elements.status.className = `bw-simulador__status is-${type}`;
      this.elements.status.textContent = message;
    }

    clearStatus() {
      this.elements.status.hidden = true;
      this.elements.status.textContent = '';
      this.elements.status.className = 'bw-simulador__status';
    }

    emptyTotals() {
      return {
        compra: 0,
        descNeto: 0,
        precioCat: 0,
        pagoRegalo: 0,
        gananciaRegalo: 0,
        basePB: 0,
        velocimetro: 0,
        extraInvitado: 0,
        pbFinal: 0,
        beneficioEfectivo: 0,
        porcentajeGanancia: 0
      };
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  function init(root = document) {
    root.querySelectorAll(SELECTOR).forEach((element) => {
      if (element.dataset.bwSimulatorInitialized === 'true') return;
      element.dataset.bwSimulatorInitialized = 'true';
      new BetterwareSimulator(element);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
